import { useState, type ReactNode } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { normalizeApiArrayResponse } from "../../shared/utility";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

export function DashboardViewModel(logout: () => void) {
  const [emptyModal, setEmptyModelOpen] = useState<{ open: boolean; childComponent: ReactNode }>({
    open: false,
    childComponent: null,
  });

  const [statsSummaries, setStatsSummaries] = useState<Record<string, unknown>[]>([]);
  const [productInventoryStats, setProductInventoryStats] = useState<Record<string, unknown>[]>([]);
  const [topSellingItemsStats, setTopSellingItemsStats] = useState<Record<string, unknown>[]>([]);
  const [topSalesPersonsStats, setTopSalesPersonsStats] = useState<Record<string, unknown>[]>([]);
  const [chartData, setChartData] = useState<Record<string, unknown>>({});

  const getStatsSummaries = async function () {
    let callResult = await makeGetApiCall("fetchStatsSummaries");

    if (callResult.success) {
      setStatsSummaries(normalizeApiArrayResponse<Record<string, unknown>>(callResult.data));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not load stats", "Please try again.");
    }
  };

  const getProductInventoryStats = async function () {
    let callResult = await makeGetApiCall("fetchInventoryStats");

    if (callResult.success) {
      setProductInventoryStats(normalizeApiArrayResponse<Record<string, unknown>>(callResult.data));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not load stats", "Please try again.");
    }
  };

  const getTopSellingItemsStats = async function () {
    let callResult = await makeGetApiCall("fetchTopSellingItemsStats");

    if (callResult.success) {
      setTopSellingItemsStats(normalizeApiArrayResponse<Record<string, unknown>>(callResult.data));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not load stats", "Please try again.");
    }
  };

  const getChartData = async function () {
    let callResult = await makeGetApiCall("fetchChartData");

    if (callResult.success) {
      setChartData(callResult.data as Record<string, unknown>);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not load stats", "Please try again.");
    }
  };

  const fetchTopFiveSalesPersons = async function () {
    let callResult = await makeGetApiCall("fetchTopFiveSalesPersons");

    if (callResult.success) {
      setTopSalesPersonsStats(normalizeApiArrayResponse<Record<string, unknown>>(callResult.data));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not load stats", "Please try again.");
    }
  };

  const showErrorModal = (title: string, subtitle: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  return {
    getStatsSummaries,
    getProductInventoryStats,
    getTopSellingItemsStats,
    fetchTopFiveSalesPersons,
    getChartData,
    statsSummaries,
    productInventoryStats,
    topSellingItemsStats,
    topSalesPersonsStats,
    chartData,
    emptyModal,
    setEmptyModelOpen,
  };
}
